export const LOCAL_DASHBOARD_HOSTS = new Set(['localhost', '127.0.0.1']);

export const isLocalDashboardHost = (hostname?: string | null): boolean =>
  Boolean(hostname && LOCAL_DASHBOARD_HOSTS.has(hostname));

export const isLocalDashboardRuntime = (): boolean =>
  typeof window !== 'undefined' &&
  isLocalDashboardHost(window.location.hostname);

// Runtime que serve o login e o seletor de contexto embutidos, em vez de
// delegar a porta de entrada ao portal. Sempre verdadeiro em LOCAL; nos demais
// ambientes só quando `NX_STANDALONE_LOGIN` é ligado explicitamente.
//
// Existe por causa do deploy preview de PR: ele roda em
// `deploy-preview-N--<site>.netlify.app`, host que nunca casa com
// isLocalDashboardRuntime() e por isso caía inteiro no portal de sandbox — sem
// sessão possível, já que o cookie do Cognito é preso a `.abbiamolog.com` e o
// preview vive fora desse domínio.
//
// Flag por env var e não por regex de hostname da Netlify porque
// `[context.deploy-preview.environment]` é um bloco próprio no netlify.toml:
// ligar lá não vaza para produção. Isso não valeria para o bloco de produção,
// compartilhado por dashboard-web, LOG e GO (ver comentário em
// isLegacyDashboardHostname abaixo).
// Host de deploy preview da Netlify: `deploy-preview-N--<site>.netlify.app` e
// `<branch>--<site>.netlify.app`. Sufixo exato, pra `netlify.app.dominio-do-mal`
// não passar.
const NETLIFY_PREVIEW_HOST_RE = /\.netlify\.app$/i;

export const isNetlifyPreviewHostname = (hostname?: string | null): boolean =>
  Boolean(hostname && NETLIFY_PREVIEW_HOST_RE.test(hostname));

export const isNetlifyPreviewRuntime = (): boolean =>
  typeof window !== 'undefined' &&
  isNetlifyPreviewHostname(window.location.hostname);

export const isStandaloneLoginRuntime = (): boolean => {
  if (isLocalDashboardRuntime()) return true;
  // Trava de PROD: build que fala com dados de produção não serve o login nem o
  // seletor embutido, mesmo se `NX_STANDALONE_LOGIN` vazar pro bloco errado do
  // netlify.toml — em `*.abbiamolog.com` o login pertence ao portal.
  //
  // A exceção é o deploy preview: ele vive em `netlify.app`, onde o cookie do
  // Cognito (preso a `.abbiamolog.com`) não alcança, então sem login embutido
  // não há sessão possível. É o caso dos blocos de branch que apontam o preview
  // pra produção. Testar em prod continua sendo decisão de quem abre a PR; o que
  // esta trava garante é que o host publicado nunca serve login próprio.
  if (process.env['NX_ENV'] === 'PROD' && !isNetlifyPreviewRuntime()) {
    return false;
  }
  return process.env['NX_STANDALONE_LOGIN'] === 'true';
};

// Domínio legado, que atendia os clientes anteriores à unificação de login.
// Depois da virada ele deixa de operar e vira porta de entrada para o portal.
//
// Detecção por hostname e não por env var porque dashboard-web, LOG e GO
// passam a buildar a mesma `main` e a ler o mesmo bloco
// [context.production.environment] do netlify.toml — uma env var lá valeria
// para os três. Segue o padrão de getDashboardProduct() em context-bootstrap.
const LEGACY_DASHBOARD_HOST_RE = /^(sandbox\.)?dashboard\.abbiamolog\.com$/i;

export const isLegacyDashboardHostname = (hostname?: string | null): boolean =>
  Boolean(hostname && LEGACY_DASHBOARD_HOST_RE.test(hostname));

export const isLegacyDashboardRuntime = (): boolean =>
  typeof window !== 'undefined' &&
  isLegacyDashboardHostname(window.location.hostname);
